import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAuth } from "@frontegg/react";
import httpClient from "utils/httpClient";
import { REMOVE_RECEIPT_ASSET } from "graphql/transaction";
import { getGraphqlErrorMessage } from "utils/graphql";
import { useReceiptExtractionProgress } from "./useReceiptExtractionProgress";
import {
  buildReceiptUploadFormData,
  validateMaxReceiptCount,
} from "utils/receipts/receiptUpload";

export type ReceiptAssetRef = {
  id: string;
  assetUrl?: string | null;
  extension?: string | null;
};

export type PendingReceiptAsset = ReceiptAssetRef & {
  name: string;
  receiptId?: string;
  processingStatus: string;
};

/**
 * Where the upload posts. The two areas differ in this and nothing else: a
 * transaction uploads through its own route, the receipt library through the
 * receipt's.
 */
export type ReceiptUploadTarget =
  | { kind: "transaction"; transactionId: string | null }
  | { kind: "receipt"; receiptId: string | null };

interface UseReceiptAssetsOptions {
  target: ReceiptUploadTarget;
  /** Receipt the assets hang off; required to remove one. */
  receiptId?: string | null;
  /** Drives max-count validation and the id list the endpoint expects. */
  existingAssets: ReceiptAssetRef[];
  /** Refetch after a successful upload or removal. */
  onChanged: () => unknown | Promise<unknown>;
}

const uploadPath = (target: ReceiptUploadTarget): string | null => {
  // Both are `publicLegacyDualRoute` on the server, so they answer with and
  // without the prefix. The versioned one is used because the unversioned mount
  // is explicitly marked for removal ("TODO: Remove after mobile is changed").
  // The comment the old hook carried said the opposite, that the route was NOT
  // dual-registered; it was wrong, and admin had been calling the bare path all
  // along without trouble.
  if (target.kind === "transaction") {
    return target.transactionId
      ? `/api/v1/transactions/${target.transactionId}/receipt`
      : null;
  }
  return target.receiptId ? `/api/v1/receipts/${target.receiptId}` : null;
};

/**
 * Upload and remove receipt assets, for the transaction flow and the receipt
 * library. `target` picks the endpoint; everything else is shared.
 *
 * **Receipts only. Do not widen this to attachments.** Payments, invoices,
 * contacts and expenses are a different system, not a different endpoint:
 * `/api/v1/attachments/{parentType}/{parentId}`, a POST per file rather than
 * one for the set, a form field of `attachment` plus name / description /
 * fileType rather than `receipt`, no max, and an `Attachment` entity that
 * carries a name where `ReceiptAsset` is `{ assetUrl, id, extension }`. That
 * path is `useAttachmentAndAssetHandling`.
 *
 * Callers own file-type validation. This checks the count only, and `accept`
 * does not apply to drag-and-drop, so a caller taking dropped files must
 * partition them first (`receiptFileTypes`).
 *
 * `removeReceiptAsset` resolves to whether the removal succeeded. It has to
 * report that rather than reject: an Apollo mutation with `onError` treats the
 * failure as handled and resolves anyway, so a caller awaiting it would run its
 * success path after a failed delete. Removing the LAST asset marks the whole
 * receipt DELETED server-side, and the receipt library closes on that, which is
 * exactly the follow-up that must not run when the delete failed.
 */
export function useReceiptAssets({
  target,
  receiptId,
  existingAssets,
  onChanged,
}: UseReceiptAssetsOptions) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [uploadingReceipts, setUploadingReceipts] = useState(false);
  const [pendingAssets, setPendingAssets] = useState<PendingReceiptAsset[]>([]);
  const previewUrls = useRef(new Set<string>());
  const generation = useRef(0);
  const path = uploadPath(target);

  useEffect(() => {
    setPendingAssets([]);
    setUploadingReceipts(false);
    const urls = previewUrls.current;
    return () => {
      generation.current += 1;
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, [path]);

  // Keep local previews until the completed receipt has been refetched.
  useEffect(() => {
    const activeUrls = new Set(pendingAssets.map((asset) => asset.assetUrl));
    previewUrls.current.forEach((url) => {
      if (!activeUrls.has(url)) {
        URL.revokeObjectURL(url);
        previewUrls.current.delete(url);
      }
    });
  }, [pendingAssets]);

  const trackReceipt = useReceiptExtractionProgress(
    target.kind === "transaction" ? target.transactionId : null,
    onChanged,
    (id, status) => {
      setPendingAssets((assets) =>
        status === "COMPLETE" || status === "FAILED"
          ? assets.filter((asset) => asset.receiptId !== id)
          : assets.map((asset) =>
              asset.receiptId === id
                ? { ...asset, processingStatus: status }
                : asset,
            ),
      );
    },
  );

  const [removeAssetMutation, { loading: removingReceiptAsset }] = useMutation(
    REMOVE_RECEIPT_ASSET,
    {
      onCompleted: () => {
        onChanged();
        toast.success(t("Receipt removed successfully"));
      },
      onError: (error) => {
        toast.error(getGraphqlErrorMessage(error?.graphQLErrors));
      },
    },
  );

  // Returns the mutation promise so a caller can sequence work after it. The
  // transaction review needs that: it re-derives compliance only when the set
  // becomes empty, which it can only know once the removal has landed.
  const removeReceiptAsset = async (assetId: string): Promise<boolean> => {
    if (!receiptId) return false;
    const result = await removeAssetMutation({
      variables: { receiptId, assetId },
    });
    return !result.errors?.length && result.data != null;
  };

  const uploadReceipts = async (files: FileList) => {
    if (!path || files.length === 0) return;

    if (
      !validateMaxReceiptCount({
        existingCount: existingAssets.length + pendingAssets.length,
        incomingCount: files.length,
      }).ok
    ) {
      toast.error(t("You can only upload 5 receipts total"));
      return;
    }

    const formData = buildReceiptUploadFormData({
      files,
      existingAssetIds: existingAssets.map((a) => a.id),
    });

    const uploadGeneration = generation.current;
    const previews: PendingReceiptAsset[] =
      target.kind === "transaction"
        ? Array.from(files, (file) => {
            const url = URL.createObjectURL(file);
            previewUrls.current.add(url);
            return {
              id: url,
              assetUrl: url,
              name: file.name,
              extension: file.name.split(".").pop(),
              processingStatus: "UPLOADING",
            };
          })
        : [];
    const previewIds = new Set(previews.map((asset) => asset.id));
    const removePreviews = () =>
      setPendingAssets((assets) =>
        assets.filter((asset) => !previewIds.has(asset.id)),
      );
    setPendingAssets((assets) => [...assets, ...previews]);

    try {
      setUploadingReceipts(true);
      const response = await httpClient.post(path, formData, {
        headers: {
          Authorization: `Bearer ${user?.accessToken}`,
          ...(target.kind === "transaction" && { Prefer: "respond-async" }),
        },
      });
      if (generation.current !== uploadGeneration) return;
      if (response.status === 202) {
        setPendingAssets((assets) =>
          assets.map((asset) =>
            previewIds.has(asset.id)
              ? {
                  ...asset,
                  receiptId: response.data.receiptId,
                  processingStatus: "QUEUED",
                }
              : asset,
          ),
        );
        trackReceipt(response.data.receiptId);
        toast.success(t("ReceiptsQueuedToast"));
      } else {
        await onChanged();
        if (generation.current !== uploadGeneration) return;
        removePreviews();
        toast.success(t("Receipts uploaded successfully!"));
      }
    } catch (err) {
      if (generation.current !== uploadGeneration) return;
      removePreviews();
      const responseData = (err as { response?: { data?: unknown } })?.response
        ?.data;
      toast.error(
        typeof responseData === "string"
          ? responseData
          : t("Failed to upload receipts"),
      );
    } finally {
      if (generation.current === uploadGeneration) setUploadingReceipts(false);
    }
  };

  return {
    uploadReceipts,
    uploadingReceipts,
    pendingAssets,
    removeReceiptAsset,
    removingReceiptAsset,
  };
}
